import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  Container,
  Link,
  IconButton,
} from '@mui/material';
import { CheckCircle as SuccessIcon, ArrowBack } from '@mui/icons-material';
import { bingService } from '../services/bingService';

const steps = ['Connect Microsoft Account', 'Select Site', 'Verify Ownership', 'Complete'];

export const BingOAuthWizard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [siteUrl, setSiteUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectedSite, setConnectedSite] = useState<any>(null);

  // Handle OAuth callback
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const storedState = sessionStorage.getItem('bing_oauth_state');

    if (code && state) {
      // Validate CSRF state
      if (state !== storedState) {
        setError('Invalid OAuth state. Possible CSRF attack.');
        return;
      }

      sessionStorage.removeItem('bing_oauth_state');
      setActiveStep(1); // Move to site selection step
    }
  }, [searchParams]);

  const handleStartOAuth = () => {
    const redirectUri = `${window.location.origin}/bing-oauth/callback`;
    bingService.initiateOAuth(redirectUri);
  };

  const handleConnectSite = async () => {
    if (!siteUrl) {
      setError('Please enter a site URL');
      return;
    }

    // Validate URL format
    try {
      new URL(siteUrl);
    } catch {
      setError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const code = searchParams.get('code');
      if (!code) {
        throw new Error('Authorization code not found');
      }

      const site = await bingService.handleOAuthCallback(code, siteUrl);
      setConnectedSite(site);
      setActiveStep(3); // Move to completion step
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to connect Bing site. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    navigate('/webtools/config'); // Redirect to Site Configuration page
  };

  const handleCancel = () => {
    navigate('/webtools/config'); // Return to Site Configuration page
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              Connect your Microsoft account to access Bing Webmaster Tools data.
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              You'll be redirected to Microsoft to authorize access. Make sure you have:
            </Typography>
            <ul>
              <li>
                <Typography variant="body2">
                  A Microsoft account with access to Bing Webmaster Tools
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Your site already added and verified in{' '}
                  <Link href="https://www.bing.com/webmasters" target="_blank" rel="noopener">
                    Bing Webmaster Tools
                  </Link>
                </Typography>
              </li>
            </ul>
            <Button
              variant="contained"
              onClick={handleStartOAuth}
              sx={{ mt: 2 }}
            >
              Connect Microsoft Account
            </Button>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              Enter the URL of your site as registered in Bing Webmaster Tools.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> The URL must exactly match what's in Bing Webmaster Tools
                (including https:// and trailing slash).
              </Typography>
            </Alert>
            <TextField
              fullWidth
              label="Site URL"
              placeholder="https://example.com/"
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
              error={!!error}
              helperText={error || 'Example: https://example.com/ or https://blog.example.com/'}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              onClick={handleConnectSite}
              disabled={loading || !siteUrl}
            >
              {loading ? <CircularProgress size={24} /> : 'Connect Site'}
            </Button>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Verifying site ownership...
            </Typography>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <SuccessIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Successfully Connected!
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Your Bing Webmaster Tools site has been connected.
            </Typography>
            {connectedSite && (
              <Alert severity="success" sx={{ mt: 2, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>Site:</strong> {connectedSite.siteUrl}
                </Typography>
                <Typography variant="body2">
                  <strong>Connected:</strong> {new Date(connectedSite.connectedAt).toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {connectedSite.status}
                </Typography>
              </Alert>
            )}
            <Button
              variant="contained"
              onClick={handleFinish}
              sx={{ mt: 3 }}
            >
              Go to Credentials Management
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 4, mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton 
            onClick={() => navigate('/webtools/config')}
            aria-label="back to site configuration"
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4">
            Connect Bing Webmaster Tools
          </Typography>
        </Box>
        
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {renderStepContent()}

        {/* Navigation Buttons */}
        <Box display="flex" justifyContent="space-between" sx={{ mt: 3 }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};
